/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { AbstractStandardOperandValueImpl, AbstractStandardOperandValueImplBaseRecordClassFactory } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/AbstractStandardOperandValueImplBaseRecordClassFactory";
import { AbImmutableRecord } from "abreact/utils/AbImmutableRecord";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { Optional } from "abweb/utils/optional/Optional";
import Immutable from "immutable";
const defaultState = {
  ...AbstractStandardOperandValueImpl.getDefaultState(),
  expExpressionNodeNodeType: undefined,
  sourceOperandValue: undefined,
  expOperatorObject: undefined,
  argumentOperandValuesByOperandName: Immutable.Map(),
  operandName: undefined,
  isPlaceholder: false,
  executionFlags: Immutable.Set()
};
export class OperatorOperandValueImpl extends new AbstractStandardOperandValueImplBaseRecordClassFactory().createBaseRecordClass(defaultState) {
  constructor(stateWithoutNodeType) {
    super({
      expExpressionNodeNodeType: ExpExpressionNodeNodeTypeEnum.OPERATOR,
      ...stateWithoutNodeType
    });
  }
  get expOperator() {
    return this.expOperatorObject.value;
  }
  get isJoin() {
    return Expression.Domain.OperatorRole.isJoin(this.expOperator);
  }
  get isCondition() {
    return Expression.Domain.OperatorRole.isCondition(this.expOperator);
  }
  get isFormula() {
    return this.valueCategoryID === Expression.Domain.Constants.CATEGORY_FORMULA;
  }
  get isSubExpression() {
    return this.valueCategoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION;
  }
  get isFunction() {
    return !this.isFormula && !this.isSubExpression;
  }
  withSourceOperandValue(sourceOperandValue) {
    return this.with({
      sourceOperandValue
    });
  }
  withoutSourceOperandValue() {
    return this.withSourceOperandValue(undefined);
  }
  withExpOperator(expOperator) {
    return this.with({
      expOperatorObject: AbImmutableRecord.State.Object.from(expOperator)
    });
  }
  withArgumentOperandValuesByOperandName(argumentOperandValuesByOperandName) {
    return this.with({
      argumentOperandValuesByOperandName
    });
  }
  withArgumentOperandValue(operandValue) {
    let {
      argumentOperandValuesByOperandName
    } = this;
    argumentOperandValuesByOperandName = argumentOperandValuesByOperandName.set(operandValue.id, operandValue);
    return this.withArgumentOperandValuesByOperandName(argumentOperandValuesByOperandName);
  }
  withoutArgumentOperandValues() {
    return this.withArgumentOperandValuesByOperandName(Immutable.Map());
  }
  withOperandName(operandName) {
    return this.with({
      operandName
    });
  }
  withExecutionFlags(executionFlags) {
    return this.with({
      executionFlags: Immutable.Set(executionFlags)
    });
  }
  transformRecursive(transformer, predicate) {
    let transformed = super.transformRecursive(transformer, predicate);
    let {
      sourceOperandValue,
      argumentOperandValuesByOperandName
    } = transformed;
    if (sourceOperandValue != null) {
      sourceOperandValue = sourceOperandValue.transformRecursive(transformer, predicate);
    }
    for (const operandName of argumentOperandValuesByOperandName.keySeq().toArray()) {
      argumentOperandValuesByOperandName = argumentOperandValuesByOperandName.set(operandName, argumentOperandValuesByOperandName.get(operandName).transformRecursive(transformer, predicate));
    }
    return transformed.with({
      sourceOperandValue: sourceOperandValue,
      argumentOperandValuesByOperandName: argumentOperandValuesByOperandName
    });
  }
  stateWithoutNodeType() {
    return {
      ...super.stateWithoutNodeType(),
      expOperatorObject: this.expOperatorObject,
      operandName: this.operandName,
      sourceOperandValue: this.sourceOperandValue,
      isPlaceholder: this.isPlaceholder,
      argumentOperandValuesByOperandName: this.argumentOperandValuesByOperandName,
      executionFlags: this.executionFlags
    };
  }
  toExpExpressionNode() {
    const expExpressionNode = super.toExpExpressionNode();
    expExpressionNode.operatorID = this.expOperator.id;
    expExpressionNode.label = this.expOperator.label;
    expExpressionNode.additionalData = this.expAdditionalData;
    expExpressionNode.operandName = this.operandName;
    const argumentOperandValues = this.argumentOperandValuesByOperandName.valueSeq().toArray();
    expExpressionNode.operandValues = Expression.Node.Producer.mapNullable(this.sourceOperandValue, ...argumentOperandValues);
    expExpressionNode.executionFlags = this.executionFlags.size > 0 ? this.executionFlags.toArray() : undefined;
    expExpressionNode.integrationConfiguration = this.expOperator.integrationConfiguration;
    return expExpressionNode;
  }
}
(function (_OperatorOperandValueImpl) {
  function getDefaultState() {
    return defaultState;
  }
  _OperatorOperandValueImpl.getDefaultState = getDefaultState;
  function isInstance(operandValue) {
    return ClassUtils.isInstanceOf(operandValue, OperatorOperandValueImpl);
  }
  _OperatorOperandValueImpl.isInstance = isInstance;
  function cast(operandValue) {
    Assert.assert(isInstance(operandValue));
    return operandValue;
  }
  _OperatorOperandValueImpl.cast = cast;
  function castNullable(operandValue) {
    return Optional.of(operandValue).map(cast).get();
  }
  _OperatorOperandValueImpl.castNullable = castNullable;
})(OperatorOperandValueImpl || (OperatorOperandValueImpl = {}));
//# sourceMappingURL=OperatorOperandValueImpl.js.map
